import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Download, FileText, Clock, Lock } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { API } from "../lib/site";
import { useAuth } from "../lib/auth";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

const WhitePaperCard = ({ post, index }) => (
  <Reveal delay={index * 0.08}>
    <div data-testid={`whitepaper-${post.id}`} className="group relative h-full bg-panel border border-white/10 hover:border-steel/50 p-8 transition-colors duration-500 flex flex-col">
      <div className="flex items-start justify-between mb-6">
        <span className="w-11 h-11 border border-steel/40 flex items-center justify-center text-steel"><FileText size={18} /></span>
        <span className="text-xs uppercase tracking-widest text-steel">{fmtDate(post.date)}</span>
      </div>
      <Link to={`/newsroom/${post.id}`} className="flex-1">
        <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-textmain group-hover:text-steel transition-colors mb-3">{post.title}</h3>
        <p className="text-sm text-textmuted leading-relaxed">{post.excerpt}</p>
      </Link>
      <div className="flex items-center gap-4 mt-6 pt-6 border-t border-white/10">
        <Link to={`/newsroom/${post.id}`} data-testid={`whitepaper-read-${post.id}`} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-textmain hover:text-steel transition-colors">
          Read <ArrowUpRight size={14} />
        </Link>
        {post.has_pdf && (
          <a href={`${API}/news/${post.id}/pdf`} target="_blank" rel="noopener noreferrer" data-testid={`whitepaper-download-${post.id}`} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-steel hover:text-steel-hover transition-colors">
            <Download size={14} /> Download
          </a>
        )}
      </div>
    </div>
  </Reveal>
);

const NewsRow = ({ post, index }) => (
  <Reveal delay={index * 0.05}>
    <Link to={`/newsroom/${post.id}`} data-testid={`news-item-${post.id}`} className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-center py-8 border-t border-white/10 hover:bg-white/[0.02] transition-colors duration-300 px-2 md:px-4">
      <div className="md:col-span-2"><span className="text-xs uppercase tracking-widest text-textmuted">{fmtDate(post.date)}</span></div>
      <div className="md:col-span-2"><span className="text-xs uppercase tracking-widest text-steel border border-steel/30 px-3 py-1">{post.category}</span></div>
      <div className="md:col-span-7">
        <h3 className="font-heading text-xl md:text-2xl font-semibold uppercase tracking-tight text-textmain group-hover:text-steel transition-colors duration-300">{post.title}</h3>
        <p className="text-sm text-textmuted mt-2 hidden md:block">{post.excerpt}</p>
      </div>
      <div className="md:col-span-1 flex md:justify-end items-center gap-2">
        <span className="text-xs text-textmuted md:hidden flex items-center gap-1"><Clock size={11} /> {post.read_time}</span>
        <ArrowUpRight size={22} className="text-textmuted group-hover:text-steel group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
      </div>
    </Link>
  </Reveal>
);

export default function Newsroom() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/news`)
      .then((r) => active && setPosts(r.data))
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const pinned = posts.filter((p) => p.pinned);
  const regular = posts.filter((p) => !p.pinned);

  return (
    <div data-testid="newsroom-page">
      <PageHero
        overline="Newsroom · Insights"
        title={["Press &", "white papers."]}
        subtitle="Announcements, technical white papers, and field insights from across the Ascendance portfolio."
      />

      {/* Admin access */}
      <div className="bg-ink border-b border-white/10">
        <div className="container-x py-4 flex justify-end">
          <Link
            to="/admin"
            data-testid="newsroom-admin-link"
            className="inline-flex items-center gap-2 text-xs font-sans font-semibold uppercase tracking-[0.2em] text-textmuted hover:text-steel border border-white/15 hover:border-steel px-4 py-2 transition-colors duration-300"
          >
            <Lock size={13} />
            {user ? "Manage Newsroom" : "Admin Login"}
          </Link>
        </div>
      </div>

      {/* Pinned White Papers */}
      {pinned.length > 0 && (
        <section className="bg-surface py-16 md:py-24" data-testid="pinned-section">
          <div className="container-x">
            <Reveal className="mb-12">
              <p className="overline mb-4">Pinned · White Papers</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-textmain">Featured research</h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pinned.map((p, i) => <WhitePaperCard key={p.id} post={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Latest news */}
      <section className="bg-ink py-16 md:py-24" data-testid="latest-section">
        <div className="container-x">
          <Reveal className="mb-8">
            <p className="overline mb-4">Latest</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-textmain">Insights & announcements</h2>
          </Reveal>
          {loading ? (
            <div className="space-y-px">{[...Array(4)].map((_, i) => <div key={i} className="h-24 border-t border-white/10 animate-pulse bg-white/[0.02]" />)}</div>
          ) : regular.length > 0 ? (
            <div className="border-b border-white/10">{regular.map((p, i) => <NewsRow key={p.id} post={p} index={i} />)}</div>
          ) : (
            <p className="text-textmuted border-t border-white/10 pt-8">No posts yet.</p>
          )}
        </div>
      </section>
    </div>
  );
}
